<template>
  <div class="flex flex-col card-content">
    <!--    顶部区域-->
    <div class="flex-0 flex items-center">
      <div class="flex-1 attr-list">
        <div
          v-for="attr in activeAttributes"
          :key="attr.chartName"
          class="attr-item"
        >
          <span
            class="color-block"
            :style="{ background: attr.color }"
          />
          <span class="text">{{ attr.text }}</span>
        </div>
      </div>
      <div class="flex-0">
        <Popover
          content-class="attr-select"
          v-model:popup-visible="popoverVisible"
          :arrow-style="{ display: 'none' }"
          position="bl"
          popup-container="#detail-main-container"
          trigger="click"
        >
          <Button
            >{{ $t('H1209') }}
            <i
              v-if="popoverVisible"
              class="iconfont iconicon_jichutubiao_jiantou_shouqi status-icon"
            />
            <i
              v-else
              class="iconfont iconicon_jichutubiao_jiantou_xiala status-icon"
            />
          </Button>
          <template #content>
            <div
              v-for="data in state.options"
              :key="data.text"
              :data-key="data.chartName"
              class="option-name"
              :class="{
                'active-option': activeAttrNames.includes(data.chartName)
              }"
              @click="handleClickOption(data)"
            >
              <div class="activity-detail-chart-attr-option">
                <span
                  class="color-block flex-shrink-0"
                  :style="{ background: data.color }"
                />
                <span class="text">{{ data.text }}</span>
              </div>
            </div>
          </template>
        </Popover>
      </div>
    </div>
    <!--    图表区域-->
    <div
      v-if="!activeAttributes.length"
      class="chart-box"
    />
    <TimeChart
      v-if="chartType === 'time'"
      v-show="activeAttributes.length"
      class="chart-box chart-box-type-time"
    />
    <DistancePolylineChart
      v-if="chartType === 'distance-polyline'"
      v-show="activeAttributes.length"
      class="chart-box chart-box-type-distance-polyline"
    />
    <index-rect-chart
      v-if="chartType === 'index-rect'"
      v-show="activeAttributes.length"
      class="chart-box chart-box-type-index-rect"
    />
    <div class="flex-0 models">
      <ButtonGroup>
        <Button
          size="mini"
          class="item"
          :class="[chartModel === item.key && 'active']"
          v-for="(item, index) in models"
          :key="item.key + '-' + index"
          @click="changeModel(item.key)"
          >{{ item.text }}</Button
        >
      </ButtonGroup>
    </div>
  </div>
</template>

<script setup>
import { watch, ref, computed, reactive, nextTick, getCurrentInstance, onMounted, onBeforeUnmount } from 'vue';
import { ButtonGroup, Button, Popover, Message, Select } from '@arco-design/web-vue';
import { activityDetailStore } from '@client/store/activityDetailStore';
import { getMaxAttrCount } from './utils/common';
import TimeChart from './time/index.vue';
import IndexRectChart from './index-rect/index.vue';
import { useActiveAttributes } from './useActiveAttributes';
import useFrameSelection from './useFrameSelection';
import { useModels } from './useModels';
import unit from '@client/helper/unit';
import DistancePolylineChart from './distance-polyline/index.vue';
import useBoardResizeEvent from '@client/hooks/useBoardResizeEvent';
import * as corosSportConfig from '@coros/sport-config';

const { INDOOR_CLIMB_BOULDERING_CHART_NAME } = corosSportConfig.sport.climb;

const { triggerResize } = useBoardResizeEvent({ debounceTimeout: 10 });

const vm = getCurrentInstance();
Message._context = vm?.appContext;
const t = vm.proxy.$root.$t;
const activityDetailStoreRef = activityDetailStore();

const { models } = useModels();

const popoverVisible = ref(false);

const sidebarOpenStatus = computed(() => activityDetailStoreRef.sidebarOpenStatus);
watch(sidebarOpenStatus, () => {
  triggerResize();
});
const chartModel = computed(() => activityDetailStoreRef.chartModel);
const chartType = computed(() => activityDetailStoreRef.chartType);
const maxAttrCount = computed(() => {
  return getMaxAttrCount(chartType.value);
});

function handleClickOption(data) {
  let valueArray = unit.clone(activeAttrNames.value);
  const value = data.chartName;
  const index = valueArray.indexOf(value);
  if (index >= 0) {
    valueArray.splice(index, 1);
  } else if (valueArray.length < maxAttrCount.value) {
    valueArray.push(value);
  } else {
    valueArray.splice(0, 1);
    valueArray.push(value);
  }

  valueArray = beforeChangeAttr(valueArray, activeAttrNames.value);
  // 这里确保难度图表在最后，不会覆盖其他图表
  const idx = valueArray.indexOf(INDOOR_CLIMB_BOULDERING_CHART_NAME);
  if (idx > -1) {
    valueArray.splice(idx, 1);
    valueArray.push(INDOOR_CLIMB_BOULDERING_CHART_NAME);
  }
  updateActiveAttrNames(valueArray);
}

const state = reactive({
  options: [],
  clientWidth: 0
});

const { sportAttrs, activeAttrNames, updateActiveAttrNames, activeAttributes } = useActiveAttributes({ setWatch: true });

function setOptions() {
  state.options = Object.keys(sportAttrs.value).map((k) => {
    return { key: k, ...sportAttrs.value[k] };
  });
}

watch(sportAttrs, () => {
  nextTick(() => {
    setOptions();
  });
});

onMounted(() => {
  nextTick(() => {
    changeModel('time');
  });
});

// 在改变显示的图表类型时，对风阻系数和功率做特殊处理
function beforeChangeAttr(attr = [], oldAttr = []) {
  if (chartType.value === 'index-rect') {
    if (attr.includes('Air_Power') && !attr.includes('Power')) {
      if (!oldAttr.includes('Air_Power')) {
        // 之前没有，现在有，说明是选择了风阻
        // 选择风阻，需要连带选中功率
        attr.splice(0, attr.length);
        attr.push('Air_Power');
        attr.push('Power');
      } else {
        // 其他情况当做是删除功率
        // 删除功率需要连带删除风阻
        attr.splice(attr.indexOf('Air_Power'), 1);
      }
    } else if (attr.length > 1 && !attr.includes('Air_Power')) {
      attr.splice(0, attr.length - 1);
    }
    return attr;
  }

  if (attr.includes('Air_Power') && !attr.includes('Power')) {
    if (!oldAttr.includes('Air_Power')) {
      // 之前没有，现在有，说明是选择了风阻
      // 选择风阻，需要连带选中功率
      if (attr.length >= 4) {
        attr = attr.filter((key) => key !== 'Air_Power');
        attr.splice(0, 1);
        attr.push('Air_Power');
      }
      attr.push('Power');
    } else {
      // 其他情况当做是删除功率
      // 删除功率需要连带删除风阻
      attr.splice(attr.indexOf('Air_Power'), 1);
    }
  }

  return attr;
}

const { resetSelect } = useFrameSelection();
// 改变模式
function changeModel(key) {
  activityDetailStoreRef.changeChartModel(key);
  resetSelect();
}
</script>

<style lang="scss" scoped>
@import '@client/styles/_mixins.scss';

.activity-detail-chart-attr-option {
  display: flex;
  flex-direction: row;
  align-items: center;

  .color-block {
    margin-right: 4px;
    width: 4px;
    height: 12px;
    border-radius: 1px;
  }

  .text {
    color: #ffffff;
    font-size: 14px;
  }
}

.card-content {
  margin-bottom: 16px;
  position: relative;
  width: 100%;
  height: 394px;
  padding: 16px;
  box-sizing: border-box;
  background: #191e30;
  border-radius: 4px;

  .attr-list {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;

    .attr-item {
      margin-right: 12px;
      display: flex;
      flex-direction: row;
      align-items: center;

      &:nth-last-of-type(1) {
        margin-right: 0;
      }

      .color-block {
        margin-right: 4px;
        width: 4px;
        height: 12px;
        border-radius: 1px;
      }

      .text {
        color: rgba(255, 255, 255, 0.8);
        font-size: 14px;
      }
    }
  }

  .chart-box {
    flex-basis: 100px;
    flex-grow: 1;
    flex-shrink: 1;
    position: relative;
    font-size: 0;
  }

  .models {
    padding-top: 8px;
    text-align: right;
    .item {
      &.active {
        background: rgba(255, 255, 255, 0.2);
      }
    }
  }
}

.option-name {
  line-height: 22px;
  margin: 4px;
  padding: 5px 8px;
  border-radius: 2px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding-left: 8px;

  &.active-option {
    background-color: #545c71;
    color: #00aaff;
  }
}
</style>

<style>
.arco-popover-popup-content.attr-select {
  padding: 2px 0;

  min-width: 180px;
  max-height: 400px;
  overflow: auto;
  font-size: 14px;
  background: #424b62;
  border: none;
}
</style>
